"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InputsPanel = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../theme");
const calculator_styles_1 = require("./calculator-styles");
const model_1 = require("./model");
const format_1 = require("./format");
const NumberField_1 = require("./NumberField");
const CalculateRow = styled_components_1.default.div `
  margin-top: ${theme_1.theme.spacing(3)};
  padding-top: ${theme_1.theme.spacing(3)};
  border-top: 1px solid ${theme_1.theme.colors.grey(4)};
`;
const CalculateButton = styled_components_1.default.button `
  width: 100%;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  ${theme_1.theme.fontLabelBold};
  font-size: 16px;
  padding: ${theme_1.theme.spacing(2)} ${theme_1.theme.spacing(3)};
  /* Amber while the results are out of date, grey only when disabled. */
  background: ${(p) => (p.$dirty ? calculator_styles_1.ATTENTION : theme_1.theme.colors.dimBlue)};
  color: ${(p) => (p.$dirty ? theme_1.theme.colors.blackText : theme_1.theme.colors.white)};
  transition: background 0.18s ease;

  &:hover:enabled {
    background: ${(p) => (p.$dirty ? "#C98D12" : theme_1.theme.colors.darkBlue)};
  }

  &:disabled {
    background: ${theme_1.theme.colors.grey(3)};
    color: ${theme_1.theme.colors.grey(1)};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${theme_1.theme.colors.blue};
    outline-offset: 2px;
  }
`;
const PrintButton = styled_components_1.default.button `
  width: 100%;
  margin-top: ${theme_1.theme.spacing(2)};
  border: 1px solid ${theme_1.theme.colors.grey(4)};
  border-radius: 4px;
  background: ${theme_1.theme.colors.white};
  color: ${theme_1.theme.colors.dimBlue};
  cursor: pointer;
  ${theme_1.theme.fontLabelBold};
  ${theme_1.theme.fontSize(-1)};
  padding: ${theme_1.theme.spacing(1)} ${theme_1.theme.spacing(3)};

  &:hover:enabled {
    border-color: ${theme_1.theme.colors.blue};
    color: ${theme_1.theme.colors.blue};
  }

  &:disabled {
    color: ${theme_1.theme.colors.grey(2)};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${theme_1.theme.colors.blue};
    outline-offset: 2px;
  }
`;
const CalculateNote = styled_components_1.default.p `
  margin: ${theme_1.theme.spacing(1)} 0 0;
  ${theme_1.theme.fontNormal};
  ${theme_1.theme.fontSize(0)};
  color: ${(p) => (p.$dirty ? "#8A6200" : theme_1.theme.colors.dimBlue)};
  line-height: 1.5;
  text-align: center;
`;
const QuoteRow = styled_components_1.default.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme_1.theme.spacing(1)};
  margin-top: ${theme_1.theme.spacing(2)};
`;
const QuoteSource = styled_components_1.default.div `
  ${theme_1.theme.fontNormal};
  ${theme_1.theme.fontSize(0)};
  color: ${theme_1.theme.colors.dimBlue};
  line-height: 1.4;

  b {
    ${theme_1.theme.fontLabelBold};
    color: ${theme_1.theme.colors.darkBlue};
  }
`;
const Dot = styled_components_1.default.span `
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
  background: ${(p) => p.$state === "live"
    ? theme_1.theme.colors.green
    : p.$state === "checking"
        ? theme_1.theme.colors.grey(3)
        : calculator_styles_1.ATTENTION};
`;
/** Sits under its field so each quote says whether it refreshed or not. */
const FieldStatus = styled_components_1.default.div `
  grid-column: 1 / -1;
  text-align: right;
  ${theme_1.theme.fontNormal};
  ${theme_1.theme.fontSize(0)};
  color: ${theme_1.theme.colors.dimBlue};
  margin: -2px 0 ${theme_1.theme.spacing(1)};
`;
const sourceLabel = (state) => {
    switch (state) {
        case "checking":
            return "checking…";
        case "live":
            return "updated just now";
        case "stale":
            return `not updated — ${model_1.FALLBACK_QUOTE_DATE} close`;
    }
};
const summary = (status) => {
    if (status.eua === "checking" || status.fx === "checking") {
        return "Checking live quotes…";
    }
    if (status.eua === "live" && status.fx === "live") {
        return "Both quotes updated. Edit either if needed.";
    }
    if (status.eua === "stale" && status.fx === "stale") {
        return "No live quotes. Both figures are editable above.";
    }
    return "One quote updated, the other is editable above.";
};
const InputsPanel = ({ fossil, raw, fuelCi, fuelEf, quoteStatus, dirty, recording, consentAnswered, onCalculate, onPrint, openHelp, onFossilChange, onFieldChange, onToggleHelp, onAsk, onRefresh, }) => ((0, jsx_runtime_1.jsxs)(calculator_styles_1.Panel, { as: "form", autoComplete: "off", onSubmit: (e) => e.preventDefault(), children: [(0, jsx_runtime_1.jsx)(calculator_styles_1.PanelTitle, { children: "Your inputs" }), (0, jsx_runtime_1.jsxs)(calculator_styles_1.PanelBody, { children: [(0, jsx_runtime_1.jsxs)(calculator_styles_1.Fieldset, { children: [(0, jsx_runtime_1.jsx)(calculator_styles_1.Legend, { children: "Fuel switch" }), (0, jsx_runtime_1.jsx)(calculator_styles_1.Field, { "$wide": true, children: (0, jsx_runtime_1.jsx)(calculator_styles_1.FieldLabel, { htmlFor: "fossil", children: "Fossil fuel replaced" }) }), (0, jsx_runtime_1.jsx)(calculator_styles_1.Field, { "$wide": true, children: (0, jsx_runtime_1.jsx)(calculator_styles_1.InputShell, { children: (0, jsx_runtime_1.jsx)("select", { id: "fossil", value: fossil, onChange: (e) => onFossilChange(e.target.value), children: model_1.FOSSIL_FUEL_ORDER.map((key) => ((0, jsx_runtime_1.jsx)("option", { value: key, children: model_1.FOSSIL_FUELS[key].name }, key))) }) }) }), (0, jsx_runtime_1.jsxs)(calculator_styles_1.Meta, { children: ["WtW intensity ", (0, format_1.formatTwo)(fuelCi), " gCO\u2082e/MJ \u00B7 CO\u2082 factor", " ", (0, format_1.formatTwo)(fuelEf), " t/t"] }), (0, jsx_runtime_1.jsx)(NumberField_1.NumberField, { field: "tons", label: "Tonnes replaced", unit: "t", hint: "Biofuel bunkered tonne for tonne", step: "10", value: raw.tons, onChange: onFieldChange }), (0, jsx_runtime_1.jsx)(NumberField_1.NumberField, { field: "ciBio", label: "Biofuel GHG intensity", unit: "g/MJ", hint: "Certified well-to-wake value", step: "0.5", value: raw.ciBio, onChange: onFieldChange, adornment: (0, jsx_runtime_1.jsx)(calculator_styles_1.HelpToggle, { type: "button", "aria-expanded": openHelp === "ciBio", "aria-controls": "help-ciBio", "aria-label": "Where do I find this number?", onClick: () => onToggleHelp("ciBio"), children: "?" }) }), openHelp === "ciBio" && ((0, jsx_runtime_1.jsxs)(calculator_styles_1.HelpBox, { id: "help-ciBio", children: [(0, jsx_runtime_1.jsx)("p", { children: "Take it from the Proof of Sustainability your supplier issues with the fuel. It is the well-to-wake E value in gCO\u2082e/MJ. Certified waste-based FAME and HVO typically land between 10 and 25." }), (0, jsx_runtime_1.jsx)(calculator_styles_1.AskLink, { type: "button", onClick: () => onAsk("Biofuel GHG intensity"), children: "Ask Ahti about this \u2192" })] }))] }), (0, jsx_runtime_1.jsxs)(calculator_styles_1.Fieldset, { children: [(0, jsx_runtime_1.jsx)(calculator_styles_1.Legend, { children: "Prices" }), (0, jsx_runtime_1.jsx)(NumberField_1.NumberField, { field: "priceFossil", label: "Fossil fuel price", unit: "$/t", step: "5", value: raw.priceFossil, onChange: onFieldChange }), (0, jsx_runtime_1.jsx)(NumberField_1.NumberField, { field: "priceBio", label: "Biofuel price", unit: "$/t", hint: "Delivered, per tonne", step: "5", value: raw.priceBio, onChange: onFieldChange })] }), (0, jsx_runtime_1.jsxs)(calculator_styles_1.Fieldset, { children: [(0, jsx_runtime_1.jsx)(calculator_styles_1.Legend, { children: "Market data" }), (0, jsx_runtime_1.jsx)(NumberField_1.NumberField, { field: "eua", label: "EUA price", unit: "\u20AC/t", step: "0.05", value: raw.eua, onChange: onFieldChange, status: (0, jsx_runtime_1.jsxs)(FieldStatus, { children: [(0, jsx_runtime_1.jsx)(Dot, { "$state": quoteStatus.eua }), sourceLabel(quoteStatus.eua)] }) }), (0, jsx_runtime_1.jsx)(NumberField_1.NumberField, { field: "poolPriceEur", label: "Pooled unit price", unit: "\u20AC/t", hint: "What a pool pays per surplus unit", step: "5", value: raw.poolPriceEur, onChange: onFieldChange }), (0, jsx_runtime_1.jsx)(NumberField_1.NumberField, { field: "fx", label: "EUR / USD", unit: "rate", step: "0.0001", value: raw.fx, onChange: onFieldChange, status: (0, jsx_runtime_1.jsxs)(FieldStatus, { children: [(0, jsx_runtime_1.jsx)(Dot, { "$state": quoteStatus.fx }), sourceLabel(quoteStatus.fx)] }) }), (0, jsx_runtime_1.jsxs)(QuoteRow, { children: [(0, jsx_runtime_1.jsx)(QuoteSource, { "aria-live": "polite", children: summary(quoteStatus) }), (0, jsx_runtime_1.jsx)(calculator_styles_1.SmallButton, { type: "button", onClick: onRefresh, children: "Refresh" })] })] }), (0, jsx_runtime_1.jsxs)(CalculateRow, { children: [(0, jsx_runtime_1.jsx)(CalculateButton, { type: "button", "$dirty": dirty, disabled: !consentAnswered, onClick: onCalculate, children: "Calculate" }), (0, jsx_runtime_1.jsx)(CalculateNote, { "$dirty": dirty, "aria-live": "polite", children: !consentAnswered
                                ? "Answer the recording question at the top of the page first. Either answer works."
                                : dirty
                                    ? "Figures changed — the results still show the last calculation."
                                    : recording
                                        ? "Results are up to date. Your figures are recorded when you calculate."
                                        : "Results are up to date." }), (0, jsx_runtime_1.jsx)(PrintButton, { type: "button", disabled: dirty, onClick: onPrint, children: "Print / save as PDF" })] })] })] }));
exports.InputsPanel = InputsPanel;
